import type { CustomerToolOption } from '../components/ToolSelector';

/** Id sent to the API (`exclusiveToolsFilter` / `toolId`) — marketplace offer when available. */
export function resolveToolFilterId(tool: Record<string, unknown>): string | null {
    const toolKey = tool._id ?? tool.id;
    if (toolKey == null || toolKey === '') return null;

    const offer = tool._marketplaceOffer ?? tool.marketplaceOffer;
    if (offer && typeof offer === 'object') {
        const offerId = (offer as { _id?: unknown; id?: unknown })._id ?? (offer as { id?: unknown }).id;
        if (offerId != null && offerId !== '') {
            return String(offerId);
        }
    }

    if (typeof tool.offer === 'string' && tool.offer.trim()) {
        return tool.offer.trim();
    }

    return String(toolKey);
}

/** Merge chat tool picker selection into the stream/orchestration toolsFilter shape. */
export function buildToolsFilterWithSelectedTool(
    toolsFilter: Record<string, unknown> | undefined,
    selectedToolId: string | undefined,
): Record<string, unknown> | undefined {
    if (!selectedToolId) {
        return toolsFilter;
    }

    return {
        offeredToolsAddedFilter: [],
        unpublishedVersionsFilter: [],
        ...toolsFilter,
        exclusiveToolsFilter: [selectedToolId],
    };
}

/** Map API tool payloads to dropdown options (stable id + display name). */
export function mapCustomerToolsForChat(toolsData: unknown): CustomerToolOption[] {
    if (!Array.isArray(toolsData)) {
        return [];
    }

    const seen = new Set<string>();
    const options: CustomerToolOption[] = [];

    for (const tool of toolsData) {
        if (!tool || typeof tool !== 'object') continue;
        if (tool.type === 'intent') continue;

        const idStr = resolveToolFilterId(tool as Record<string, unknown>);
        if (!idStr) continue;

        if (seen.has(idStr)) continue;
        seen.add(idStr);

        const name =
            (typeof tool.name === 'string' && tool.name.trim()) ||
            (typeof tool.label === 'string' && tool.label.trim()) ||
            idStr;

        options.push({
            id: idStr,
            name,
            description: typeof tool.description === 'string' ? tool.description : undefined,
        });
    }

    return options.sort((a, b) => a.name.localeCompare(b.name));
}
